import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BsBuilding, BsCashCoin, BsJournalCheck, BsShieldCheck } from "react-icons/bs";
import { ConfirmDialog, SaveBar } from "../../Components/Kit";
import SiteDetailsTab from "./tabs/SiteDetailsTab";
import SupportTab from "./tabs/SupportTab";
import PaymentTab from "./tabs/PaymentTab";
import TaxTab from "./tabs/TaxTab";
import SegmentTab from "./tabs/SegmentTab";
import VerificationTab from "./tabs/VerificationTab";
import BookingTimingTab from "./tabs/BookingTimingTab";
import BookingPolicyTab from "./tabs/BookingPolicyTab";
import DeliveryRadiusTab from "./tabs/DeliveryRadiusTab";
import DocumentsTab from "./tabs/DocumentsTab";
import { UnsavedProvider, discardAll, saveAll, useLeaveGuard, useUnsavedState } from "./unsaved";
import "./settings.css";

// Business Settings (redesign R8.3): the ten sections grouped into four areas, each section with a line on
// what it changes. Only the open section is mounted, so a visit fires one load, not ten. While a section
// has unsaved changes a save bar shows, and leaving the section or the page asks first (R8.4).
// URL: /settings?area=<area>&tab=<section>.
const AREAS = [
    {
        key: "business", name: "Business", icon: <BsBuilding />, intro: "Who you are to customers: the site's name and logo, and how they reach support.",
        tabs: [
            { key: "site", name: "Site details", help: "The name, title, logo and icon shown on the website, the apps and in emails.", Component: SiteDetailsTab },
            { key: "support", name: "Support contacts", help: "The emails and phone numbers customers see when they need help.", Component: SupportTab },
        ],
    },
    {
        key: "pricing", name: "Pricing & tax", icon: <BsCashCoin />, intro: "What a booking costs and how it is paid.",
        tabs: [
            { key: "payment", name: "Payment gateway", help: "The Razorpay keys every payment and refund goes through. A wrong key stops payments, so change it with care.", Component: PaymentTab },
            { key: "tax", name: "Tax & fees", help: "Added to every booking at checkout: GST and the convenience fee (percentages), the platform fee and DOP (flat amounts).", Component: TaxTab },
            { key: "segment", name: "Segment pricing", help: "The refundable security deposit for each car segment and sub-segment (hatchback, sedan, SUV ...).", Component: SegmentTab },
        ],
    },
    {
        key: "booking", name: "Booking rules", icon: <BsJournalCheck />, intro: "When customers can book, what happens if they cancel or run late, and how far you deliver.",
        tabs: [
            { key: "booking-timing", name: "Booking timing", help: "How soon a booking can start (per app) and how short or far ahead a trip can be.", Component: BookingTimingTab },
            { key: "booking-policy", name: "Booking policy", help: "Payment expiry, cancellation refunds, late returns, extensions and balance reminders.", Component: BookingPolicyTab },
            { key: "delivery-radius", name: "Delivery radius", help: "How far from a hub the car is delivered, and the distance bands its fee is worked out in.", Component: DeliveryRadiusTab },
        ],
    },
    {
        key: "verification", name: "Verification & documents", icon: <BsShieldCheck />, intro: "What customers must prove before they drive, and the policies they accept.",
        tabs: [
            { key: "verification", name: "Customer verification", help: "Which documents customers verify (Aadhaar, driving licence, a third ID), grace days after expiry and daily attempts.", Component: VerificationTab },
            { key: "documents", name: "Policies & documents", help: "Terms, policies and other written documents: which sites show each one, and when it is published.", Component: DocumentsTab },
        ],
    },
];
// The old tab keys (the ten tabs) still open the right place.
const findTab = (key) => {
    for (const area of AREAS) {
        const tab = area.tabs.find((t) => t.key === key);
        if (tab) return { area, tab };
    }
    return null;
};

const SettingsBody = () => {
    const [params, setParams] = useSearchParams();
    const state = useUnsavedState();
    const panelRef = useRef(null);
    const [pending, setPending] = useState(null); // { area, tab } waiting for "leave without saving?"
    const [saving, setSaving] = useState(false);
    useLeaveGuard(state.dirty, panelRef);

    const fromTab = findTab(params.get("tab"));
    const area = fromTab?.area || AREAS.find((a) => a.key === params.get("area")) || AREAS[0];
    const tab = fromTab && fromTab.area === area ? fromTab.tab : area.tabs[0];
    const Active = tab.Component;

    const go = (areaKey, tabKey) => {
        const next = { area: areaKey, tab: tabKey };
        if (state.dirty) { setPending(next); return; }
        setParams(next);
    };
    const leave = () => {
        discardAll(state.entries);
        setParams(pending);
        setPending(null);
    };
    const saveNow = async () => {
        setSaving(true);
        await saveAll(state.entries);
        setSaving(false);
    };

    return (
        <div className="settings-page bs-page">
            <div className="mb-3">
                <h4 className="fw-semibold mb-1">Business Settings</h4>
                <p className="text-muted mb-0">How the platform looks, charges, takes bookings and verifies customers.</p>
            </div>

            <nav className="bs-areas" aria-label="Settings areas">
                {AREAS.map((a) => (
                    <button key={a.key} type="button" className={`bs-area ${a.key === area.key ? "is-active" : ""}`} aria-current={a.key === area.key ? "page" : undefined} onClick={() => go(a.key, a.tabs[0].key)}>
                        <span className="bs-area-icon" aria-hidden="true">{a.icon}</span>
                        <span className="bs-area-text">
                            <span className="bs-area-name">{a.name}</span>
                            <span className="bs-area-sub">{a.tabs.map((t) => t.name).join(", ")}</span>
                        </span>
                    </button>
                ))}
            </nav>

            <div className="bs-body">
                <div className="bs-side">
                    <p className="bs-intro">{area.intro}</p>
                    <div className="bs-tabs" role="tablist" aria-orientation="vertical" aria-label={area.name}>
                        {area.tabs.map((t) => (
                            <button key={t.key} type="button" role="tab" aria-selected={t.key === tab.key} className={`bs-tab ${t.key === tab.key ? "is-active" : ""}`} onClick={() => go(area.key, t.key)}>{t.name}</button>
                        ))}
                    </div>
                </div>
                <div className="bs-panel" ref={panelRef}>
                    <div className="bs-help" role="note">{tab.help}</div>
                    <Active key={tab.key} />
                    <SaveBar show={state.dirty} saving={saving} onSave={saveNow} onDiscard={() => discardAll(state.entries)} text={Object.keys(state.entries).length > 1 ? `Unsaved changes in ${Object.keys(state.entries).length} sections` : "You have unsaved changes"} />
                </div>
            </div>

            <ConfirmDialog
                open={!!pending}
                title="Leave without saving?"
                text="The changes on this section are not saved yet. Leaving undoes them."
                confirmLabel="Leave without saving"
                danger
                onConfirm={leave}
                onCancel={() => setPending(null)}
            />
        </div>
    );
};

const Settings = () => (
    <UnsavedProvider>
        <SettingsBody />
    </UnsavedProvider>
);

export default Settings;
